import { useEffect, useRef } from 'react'

// The agent's on-screen voice during a live call (replaces the M2.2 avatar
// orb). Bar heights are written per frame from the SDK's real output
// spectrum, so the movement *is* the agent's speech rather than a decorative
// loop, and pyrite rings ripple outward around it — the wave sits inside the
// smallest of them — only while it's speaking. When the
// agent is listening the bars rest low and still: the participant's own mic is
// deliberately not visualized — nothing on screen should make them watch
// themselves mid-answer.

// Nine bars mirrored from five spectrum buckets, so the loudest low
// frequencies sit centre and the shape stays symmetric.
const BAR_BUCKETS = [4, 3, 2, 1, 0, 1, 2, 3, 4]

// Widening slices of the lower bins, where speech energy actually lives.
// Linear buckets across all 1024 bins would leave the outer bars flat.
const BUCKET_RANGES = [
  [0, 16],
  [16, 48],
  [48, 112],
  [112, 240],
  [240, 512],
]

const BAR_MIN = 8 // px — an 8px pill is a perfect circle: the resting dot

// The wave's envelope: each bucket's own full height, tallest at the centre.
// Giving the outer bars a lower ceiling than the middle ones makes the
// centre-tall silhouette a property of the design instead of a hopeful side
// effect of whatever the spectrum happens to do — which is what lets the rings
// enclose the wave. Rounded caps included, no tip reaches past the 80px radius
// of the smallest ripple (main.css .wave__ring), so the bars stay inside it.
const BUCKET_MAX = [96, 88, 80, 72, 56]

// The compact wave, beside the agent's name on the in-call card: a quarter
// the size, no rings (main.css .wave--compact hides them), same spectrum.
const BAR_MIN_COMPACT = 4
const BUCKET_MAX_COMPACT = [24, 22, 20, 18, 14]

const ATTACK = 0.5 // rise fast, so syllables land
const RELEASE = 0.12 // fall slowly, so pauses read as settling, not flicker

// Levels are normalized against a running reference level rather than a fixed
// gain: how hot the analyser runs depends on the voice, the device and the
// participant's volume slider, and a guessed constant either pins every bar at
// full height or never lifts them off the floor. The reference must move
// slowly in BOTH directions — one that snapped up to each frame's maximum
// would divide the loudest bar by itself every frame and freeze it at full
// height, cancelling out the loudness changes that make the wave move. The
// floor keeps silence (raw ≈ 0) from being amplified into a full wave, and the
// headroom leaves room above the typical peak so only real peaks top out.
const PEAK_RISE = 0.03 // adapts up over roughly a second
const PEAK_FALL = 0.004 // and back down over several
const PEAK_FLOOR = 0.06
const HEADROOM = 1.7

// Speech energy falls off steeply with frequency, and the reference level is
// set by the loudest (lowest) bucket — so without a tilt the outer bars sit at
// rest through the whole call and the wave reads as five bars flanked by four
// dead dots. These lift each bucket back into a usable range while keeping the
// centre tallest.
const BUCKET_TILT = [1, 1.1, 1.25, 1.5, 1.8]

// A still profile for reduced-motion: shaped like the wave, but frozen.
const STATIC_LEVELS = [0.35, 0.26, 0.18, 0.12, 0.08]

// Heights are set rather than transformed on purpose — scaleY would squash
// the pill caps out of round at every level but one.
function setBarLevel(el, bucket, level, compact) {
  if (!el) return
  const min = compact ? BAR_MIN_COMPACT : BAR_MIN
  const max = compact ? BUCKET_MAX_COMPACT[bucket] : BUCKET_MAX[bucket]
  el.style.height = `${min + level * (max - min)}px`
}

export default function AgentWave({ state, getFrequencyData, compact = false }) {
  const barsRef = useRef([])
  const levelsRef = useRef(BUCKET_RANGES.map(() => 0))
  const rawRef = useRef(BUCKET_RANGES.map(() => 0))
  // null until the first frame carrying real audio. Deliberately survives the
  // gaps between turns: the output level is a property of the call, not of one
  // turn, so re-seeding it every time the agent starts talking would slam the
  // wave to full height for a second at the top of every answer.
  const peakRef = useRef(null)

  useEffect(() => {
    const bars = barsRef.current
    const levels = levelsRef.current
    const raw = rawRef.current

    // Clearing the inline height hands the bars back to the stylesheet's
    // resting size, which is the whole of the "listening" behaviour.
    const rest = () => {
      levels.fill(0)
      raw.fill(0)
      bars.forEach((el) => {
        if (el) el.style.height = ''
      })
    }

    if (state !== 'speaking') {
      rest()
      return undefined
    }

    // main.css's blanket prefers-reduced-motion rule can't stop a rAF loop,
    // so opt out here instead of animating past someone's setting.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      bars.forEach((el, i) =>
        setBarLevel(el, BAR_BUCKETS[i], STATIC_LEVELS[BAR_BUCKETS[i]], compact),
      )
      return undefined
    }

    let frame = 0
    const draw = () => {
      frame = requestAnimationFrame(draw)
      // Null while the analyser is still coming up — the bars just settle.
      const data = getFrequencyData()

      let frameMax = 0
      BUCKET_RANGES.forEach(([from, to], i) => {
        raw[i] = 0
        if (data) {
          const last = Math.min(to, data.length)
          let sum = 0
          for (let bin = from; bin < last; bin += 1) sum += data[bin]
          raw[i] = sum / Math.max(1, last - from) / 255
        }
        if (raw[i] > frameMax) frameMax = raw[i]
      })

      const peak = peakRef.current
      if (peak === null) {
        // Seed on the first frame with real audio in it. Until then normalize
        // against the frame itself, so the wave opens at a sane height instead
        // of clamping flat out while a from-zero reference catches up.
        if (frameMax > PEAK_FLOOR) peakRef.current = frameMax
      } else {
        peakRef.current = peak + (frameMax - peak) * (frameMax > peak ? PEAK_RISE : PEAK_FALL)
      }
      const reference = peakRef.current ?? frameMax
      const scale = Math.max(reference, PEAK_FLOOR) * HEADROOM

      raw.forEach((value, i) => {
        const target = Math.min(1, (value * BUCKET_TILT[i]) / scale)
        levels[i] += (target - levels[i]) * (target > levels[i] ? ATTACK : RELEASE)
      })

      bars.forEach((el, i) => setBarLevel(el, BAR_BUCKETS[i], levels[BAR_BUCKETS[i]], compact))
    }
    draw()

    return () => {
      cancelAnimationFrame(frame)
      rest()
    }
  }, [state, getFrequencyData, compact])

  return (
    <div className={`wave wave--${state}${compact ? ' wave--compact' : ''}`} aria-hidden="true">
      <span className="wave__ring" />
      <span className="wave__ring" />
      <div className="wave__bars">
        {BAR_BUCKETS.map((_, i) => (
          <span
            key={i}
            className="wave__bar"
            ref={(el) => {
              barsRef.current[i] = el
            }}
          />
        ))}
      </div>
    </div>
  )
}
